import Button from "./Button"
import { Dispatch, SetStateAction } from "react"
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { UploadStatus } from "@/modals/ImportModal"
import { Link } from "@inertiajs/react"
import {
  CheckCircleIcon,
  ChevronRightIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid"

const generateTitle = ({
  white_player_name,
  black_player_name,
}: FulfilledUploadResult["value"]) => {
  if (!white_player_name && !black_player_name) return "Untitled game"

  const whitePlayerName = white_player_name ?? "Unknown"
  const blackPlayerName = black_player_name ?? "Unknown"

  return `${whitePlayerName} - ${blackPlayerName}`
}

type FulfilledUploadResult = {
  status: "fulfilled"
  value: {
    id: string
    white_player_name: string | null
    black_player_name: string | null
  }
}

type RejectedUploadResult = {
  status: "rejected"
  index: number
}

export type UploadResult = FulfilledUploadResult | RejectedUploadResult

const GameUploadLoader = ({
  done,
  status,
  setStatus,
  tryAgain,
  uploadResults,
}: {
  done: () => void
  status: UploadStatus
  setStatus: Dispatch<SetStateAction<UploadStatus>>
  tryAgain: () => Promise<void>
  uploadResults: UploadResult[]
}) => {
  const successfulUploads = uploadResults.filter(
    (result) => result.status === "fulfilled"
  )

  const failedUploads = uploadResults.filter(
    (result) => result.status === "rejected"
  )

  return (
    <div className="mt-4 sm:mt-6">
      {["uploading", "error"].includes(status) && (
        <div className="flex flex-col items-center justify-center space-y-3 py-4">
          <div
            className={clsx(
              "flex h-16 w-16 shrink-0 items-center justify-center rounded-full",
              {
                "bg-red-200": status === "error",
                "bg-gray-200": status === "uploading",
                "bg-green-200": status === "finished",
              }
            )}
            aria-hidden="true"
          >
            {status === "uploading" && (
              <ArrowPathIcon className="h-10 w-10 animate-spin text-gray-600" />
            )}
            {status === "error" && (
              <XMarkIcon className="h-10 w-10 text-red-600" />
            )}
          </div>
          <h3 className="text-center font-medium text-gray-900">
            {status === "uploading" && "Uploading..."}
            {status === "error" && "Upload failed."}
          </h3>
        </div>
      )}

      {status === "finished" && (
        <>
          {Boolean(successfulUploads.length) && (
            <div className="text-gray-700">
              <span className="font-semibold">{successfulUploads.length}</span>{" "}
              game{successfulUploads.length > 1 ? "s" : ""} uploaded
              successfully.
            </div>
          )}
          {Boolean(failedUploads.length) && (
            <div className="mt-1">
              <span className="font-semibold">{failedUploads.length}</span> game
              {failedUploads.length > 1 ? "s" : ""} failed to upload.
            </div>
          )}

          <ol className="mt-4 max-h-[550px] divide-y divide-gray-300 overflow-x-hidden overflow-y-auto rounded-lg border border-gray-300 sm:mt-6">
            {uploadResults.map((result) => {
              const successful = result.status === "fulfilled"

              const key = successful ? result.value.id : result.index

              if (successful) {
                return (
                  <li key={key}>
                    <Link
                      href={`/games/${result.value.id}`}
                      className="group z-10 flex w-full items-center space-x-3 bg-white px-3 py-2.5 text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none sm:space-x-2"
                    >
                      <CheckCircleIcon className="size-5 text-green-500" />
                      <span className="flex-1 text-gray-900">
                        {generateTitle(result.value)}
                      </span>
                      <ChevronRightIcon className="size-4 text-gray-500" />
                    </Link>
                  </li>
                )
              }

              return (
                <li
                  key={key}
                  className="flex w-full items-center space-x-3 bg-white px-3 py-2.5 text-sm sm:space-x-2"
                >
                  <XCircleIcon className="size-5 text-red-400" />
                  <span className="flex-1">
                    Game {result.index + 1} failed to upload.
                  </span>
                </li>
              )
            })}
          </ol>
        </>
      )}

      <div className="mt-4 grid grid-cols-3 items-center gap-2.5 sm:mt-6">
        {status === "uploading" && (
          <Button fullWidth className="col-span-3" disabled style="secondary">
            Uploading...
          </Button>
        )}
        {status === "finished" && (
          <Button fullWidth className="col-span-3" onClick={done}>
            Done
          </Button>
        )}
        {status === "error" && (
          <>
            <Button
              fullWidth
              style="secondary"
              onClick={() => setStatus("waiting")}
            >
              Back
            </Button>
            <Button fullWidth onClick={tryAgain} className="col-span-2">
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default GameUploadLoader
